import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  ModalController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { businessOutline, closeOutline, personOutline } from 'ionicons/icons';

import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { PlanChooserMessage } from '../../../../../common/enums/plan-chooser.enum';
import {
  billingIntervalRank,
  billingIntervalSuffix,
} from '../../../../../common/enums/billing-interval-label.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import {
  OrgTier,
  OrganizationService,
} from '../../../access-control/authentication/register-organization/organization.service';
import { PaymentPlanDto } from '../../models/payment.models';
import { CheckoutService } from '../../services/checkout.service';

/** What the buyer picked. The chooser owns no purchase logic — it hands this back
 *  to the paywall, which completes an individual purchase with its own CTAs, or
 *  routes an org choice to the existing register-organization flow (ADR-0123). */
export type PlanChoice =
  | { kind: 'individual'; planId: string }
  | { kind: 'org'; tierId: string };

type PlanGroup = 'individual' | 'org';

/**
 * Plan-group chooser (ADR-0123). Groups plans by AUDIENCE — an "individual" group
 * and an "organisation" group — so a buyer picks who a plan is for before which
 * plan, collapsing an N×M matrix into a short list. Opened as a modal over the
 * paywall from its "Buying for a team?" link, which only renders on web with orgs
 * enabled — so this is web-only and prices individual plans from the web geo
 * price (never a native store price). It is a pure selector: Continue dismisses
 * with a PlanChoice; the paywall does the buying.
 */
@Component({
  selector: 'app-plan-chooser',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonContent,
    IonButton,
    IonIcon,
    IonLabel,
    IonSpinner,
    IonRadio,
    IonRadioGroup,
    IonSegment,
    IonSegmentButton,
  ],
  templateUrl: './plan-chooser.component.html',
  styleUrls: ['./plan-chooser.component.scss'],
})
@ComponentAccess('PlanChooserComponent')
export class PlanChooserComponent implements OnInit {
  private readonly modalCtrl = inject(ModalController);
  private readonly checkout = inject(CheckoutService);
  private readonly organizations = inject(OrganizationService);
  private readonly logger = inject(LogFactory).createLogger(PlanChooserComponent);

  protected readonly copy = PlanChooserMessage;

  protected readonly individualPlans = signal<PaymentPlanDto[]>([]);
  protected readonly orgTiers = signal<OrgTier[]>([]);
  protected readonly loading = signal<boolean>(true);

  protected readonly activeGroup = signal<PlanGroup>('individual');
  protected readonly selectedPlanId = signal<string | null>(null);
  protected readonly selectedTierId = signal<string | null>(null);

  /** The org group is offered only when it has published tiers — chrome appears
   *  only when it has more than one option (the segment otherwise stays hidden). */
  protected readonly showSegment = computed(() => this.orgTiers().length > 0);

  /** Continue needs a selection in the active group. */
  protected readonly canContinue = computed(() =>
    this.activeGroup() === 'individual'
      ? !!this.selectedPlanId()
      : !!this.selectedTierId(),
  );

  constructor() {
    addIcons({ closeOutline, personOutline, businessOutline });
  }

  async ngOnInit(): Promise<void> {
    // Both groups load in parallel; each is best-effort so one failing still
    // renders the other. Individual plans are cadence-sorted to match the paywall
    // (day → week → month → year), not the incidental API order.
    const [plans, tiers] = await Promise.all([
      this.checkout.eligiblePlans().catch((err) => {
        this.logger.error('Failed to load individual plans', err);
        return [] as PaymentPlanDto[];
      }),
      this.loadOrgTiers(),
    ]);

    const sorted = [...plans].sort(
      (a, b) =>
        billingIntervalRank(a.billingInterval) - billingIntervalRank(b.billingInterval),
    );
    this.individualPlans.set(sorted);
    this.selectedPlanId.set(sorted[0]?.id ?? null);

    this.orgTiers.set(tiers);
    this.selectedTierId.set(tiers[0]?.id ?? null);

    this.loading.set(false);
  }

  private async loadOrgTiers(): Promise<OrgTier[]> {
    try {
      return await new Promise<OrgTier[]>((resolve) => {
        this.organizations.getOrgTiers().subscribe({
          next: (tiers) => resolve(tiers),
          error: (err) => {
            this.logger.error('Failed to load organisation seat tiers', err);
            resolve([]);
          },
        });
      });
    } catch (err) {
      this.logger.error('Failed to load organisation seat tiers', err);
      return [];
    }
  }

  protected onGroupChange(value: string): void {
    this.activeGroup.set(value === 'org' ? 'org' : 'individual');
  }

  protected onSelectPlan(id: string): void {
    this.selectedPlanId.set(id);
  }

  protected onSelectTier(id: string): void {
    this.selectedTierId.set(id);
  }

  /** Billing-period suffix, e.g. "/ month" (App Store Guideline 3.1.2(c)). */
  protected frequencyLabel(interval?: string | null, intervalCount?: number | null): string {
    return billingIntervalSuffix(interval, intervalCount);
  }

  /** Web geo price for an individual plan (no native store price on this surface). */
  protected planPrice(plan: PaymentPlanDto): string {
    return this.formatPrice(plan.effectivePrice, plan.currency);
  }

  protected tierPrice(tier: OrgTier): string {
    return this.formatPrice(tier.price, tier.currency);
  }

  /** Per-seat price — the number a team buyer actually compares tiers on. */
  protected tierPerSeat(tier: OrgTier): string {
    if (!tier.seats) return '';
    return `${this.formatPrice(tier.price / tier.seats, tier.currency)}${this.copy.PerSeatSuffix}`;
  }

  protected seatsLabel(tier: OrgTier): string {
    return `${tier.seats} ${this.copy.SeatsSuffix}`;
  }

  private formatPrice(amount: number | null, currency: string): string {
    if (amount == null) return '';
    const code = currency || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'code',
      }).format(amount);
    } catch {
      return `${code} ${amount.toFixed(2)}`;
    }
  }

  protected continue(): void {
    if (this.activeGroup() === 'org') {
      const tierId = this.selectedTierId();
      if (tierId) void this.modalCtrl.dismiss({ kind: 'org', tierId });
      return;
    }
    const planId = this.selectedPlanId();
    if (planId) void this.modalCtrl.dismiss({ kind: 'individual', planId });
  }

  protected dismiss(): Promise<boolean> {
    return this.modalCtrl.dismiss();
  }
}
